import React, { useCallback, useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import { ChevronLeftIcon, ChevronRightIcon } from '~shared/assets/icons/chevrons';

const Pagination: React.FC<React.ComponentProps<'nav'>> = ({ className, ...props }) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={twMerge('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={twMerge('flex flex-row items-center space-x-1', className)} {...props} />
  )
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={twMerge('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
} & React.ComponentProps<'a'>;

const PaginationLink: React.FC<PaginationLinkProps> = ({
  className,
  children,
  isActive = false,
  disabled = false,
  ...props
}) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={twMerge(
      'flex size-7 items-center justify-center rounded-[8px] text-sm shadow-[inset_0_0_0_1px]',
      isActive ? 'bg-blue shadow-blue' : 'bg-transparent shadow-foreground',
      disabled && 'pointer-events-none',
      className
    )}
    {...props}
  >
    {children}
  </a>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious: React.FC<React.ComponentProps<typeof PaginationLink>> = ({ className, ...props }) => (
  <PaginationLink aria-label="Go to previous page" className={className} {...props}>
    <ChevronLeftIcon className="size-7" />
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext: React.FC<React.ComponentProps<typeof PaginationLink>> = ({ className, ...props }) => (
  <PaginationLink aria-label="Go to next page" className={className} {...props}>
    <ChevronRightIcon className="size-7" />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis: React.FC<React.ComponentProps<'span'>> = ({ className, ...props }) => (
  <span
    aria-hidden
    className={twMerge(
      'flex size-7 cursor-default items-center justify-center rounded-[8px] bg-transparent shadow-[inset_0_0_0_1px] shadow-foreground',
      className
    )}
    {...props}
  >
    <span>...</span>
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

interface PaginationProps {
  className?: string;
  total: number;
  perPage: number;
  page: number;
  onPageChange: (newPage: number) => void;
}

const ControlledPagination: React.FC<PaginationProps> = ({ className, page, perPage, total, onPageChange }) => {
  const totalPages = useMemo(() => {
    return Math.ceil(total / perPage);
  }, [perPage, total]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const handlePreviousPage = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      handlePageChange(Math.max(page - 1, 1));
    },
    [page, handlePageChange]
  );

  const handleNextPage = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      handlePageChange(Math.min(page + 1, total));
    },
    [page, total, handlePageChange]
  );

  if (totalPages < 2) return null;

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious href="#" disabled={page === 1} onClick={handlePreviousPage} />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink
            href="#"
            onClick={(event) => {
              event.preventDefault();
              handlePageChange(1);
            }}
            isActive={page === 1}
          >
            1
          </PaginationLink>
        </PaginationItem>
        {page >= 4 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        {page > 2 && (
          <PaginationItem>
            <PaginationLink href="#" onClick={handlePreviousPage}>
              {page - 1}
            </PaginationLink>
          </PaginationItem>
        )}
        {page !== 1 && page !== totalPages && (
          <PaginationItem>
            <PaginationLink href="#" isActive>
              {page}
            </PaginationLink>
          </PaginationItem>
        )}
        {page < totalPages - 1 && (
          <PaginationItem>
            <PaginationLink href="#" onClick={handleNextPage}>
              {page + 1}
            </PaginationLink>
          </PaginationItem>
        )}
        {page <= totalPages - 3 && (
          <PaginationItem>
            <PaginationEllipsis />
          </PaginationItem>
        )}
        <PaginationItem>
          <PaginationLink
            href="#"
            onClick={(event) => {
              event.preventDefault();
              handlePageChange(totalPages);
            }}
            isActive={page === totalPages}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext href="#" disabled={page === totalPages} onClick={handleNextPage} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export {
  ControlledPagination,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
